import type { Person } from "../models/types";
import { treeLabel } from "../utils/calendar";

export interface TreeNodeLayout {
  personId: string;
  x: number;
  y: number;
  label: string;
  diameter: number;
}

export interface TreeEdge {
  from: string;
  to: string;
}

export interface TreeLayoutResult {
  nodes: TreeNodeLayout[];
  edges: TreeEdge[];
  width: number;
  height: number;
}

const COUSIN_SLOT_DEPTH = 96;
const PAIR_SLOT_EXTRA = 36;
const BRANCH_GAP = 32;
const HEART_GAP = 16;
const H_SPACING = 172;
const GEN0_X = 76;
const LABEL_BLOCK_HEIGHT = 34;
const LANE_HEIGHT = 118;
const CHILD_SPACING = 48;

function nodeDiameter(person: Person): number {
  if (person.is_partner) {
    switch (person.generation) {
      case 1:
        return 56;
      case 3:
        return 34;
      default:
        return 40;
    }
  }
  switch (person.generation) {
    case 0:
      return 88;
    case 1:
      return 64;
    case 3:
      return 44;
    default:
      return 52;
  }
}

function footprintRadius(person: Person): number {
  if (person.is_partner) {
    switch (person.generation) {
      case 1:
        return 42;
      case 3:
        return 28;
      default:
        return 36;
    }
  }
  switch (person.generation) {
    case 0:
      return 58;
    case 1:
      return 46;
    case 3:
      return 38;
    default:
      return 52;
  }
}

function birthOrder(a: Person, b: Person): number {
  const ay = a.birth_year ?? 9999;
  const by = b.birth_year ?? 9999;
  if (ay !== by) return ay - by;
  const am = Number((a.birth_month_year ?? "").split("-")[1] ?? 99);
  const bm = Number((b.birth_month_year ?? "").split("-")[1] ?? 99);
  if (am !== bm) return am - bm;
  const ad = a.birth_day ?? 99;
  const bd = b.birth_day ?? 99;
  if (ad !== bd) return ad - bd;
  return a.display_name.localeCompare(b.display_name);
}

function pairSpan(primary: Person, partner: Person): number {
  return nodeDiameter(primary) / 2 + HEART_GAP + nodeDiameter(partner) / 2;
}

function pairSpanVertical(primary: Person, partner: Person): number {
  const topHalf = nodeDiameter(primary) / 2 + LABEL_BLOCK_HEIGHT / 2;
  const bottomHalf = nodeDiameter(partner) / 2 + LABEL_BLOCK_HEIGHT / 2;
  return topHalf + HEART_GAP + bottomHalf;
}

function placePair(
  primaryId: string,
  partnerId: string | undefined,
  centerX: number,
  y: number,
  positions: Map<string, { x: number; y: number }>,
  byId: Map<string, Person>,
) {
  const primary = byId.get(primaryId);
  if (!primary) return;
  const partner = partnerId ? byId.get(partnerId) : undefined;
  if (partner) {
    const span = pairSpan(primary, partner);
    positions.set(primaryId, { x: centerX - span / 2, y });
    positions.set(partner.person_id, { x: centerX + span / 2, y });
  } else {
    positions.set(primaryId, { x: centerX, y });
  }
}

function placePairVertical(
  primaryId: string,
  partnerId: string | undefined,
  x: number,
  centerY: number,
  positions: Map<string, { x: number; y: number }>,
  byId: Map<string, Person>,
) {
  const primary = byId.get(primaryId);
  if (!primary) return;
  const partner = partnerId ? byId.get(partnerId) : undefined;
  if (partner) {
    const span = pairSpanVertical(primary, partner);
    positions.set(primaryId, { x, y: centerY - span / 2 });
    positions.set(partner.person_id, { x, y: centerY + span / 2 });
  } else {
    positions.set(primaryId, { x, y: centerY });
  }
}

function gen3ChildIds(parentId: string, people: Person[]): string[] {
  return people
    .filter(
      (p) =>
        p.generation === 3 &&
        !p.is_partner &&
        !p.is_expected &&
        p.parent_ids.includes(parentId),
    )
    .sort(birthOrder)
    .map((p) => p.person_id);
}

function childRowWidth(childIds: string[]): number {
  if (childIds.length === 0) return 0;
  return Math.max(childIds.length - 1, 0) * CHILD_SPACING + 44;
}

function placeChildrenRow(
  childIds: string[],
  startX: number,
  y: number,
  positions: Map<string, { x: number; y: number }>,
) {
  let x = startX;
  for (const childId of childIds) {
    positions.set(childId, { x, y });
    x += CHILD_SPACING;
  }
}

interface BranchSlot {
  cousinId: string;
  partnerId?: string;
  depth: number;
}

function branchSlots(
  gen1Person: Person,
  people: Person[],
  byId: Map<string, Person>,
): BranchSlot[] {
  const branchId = gen1Person.branch_id;
  const cousins: Person[] = [];
  const seen = new Set<string>();

  for (const childId of gen1Person.child_ids) {
    const person = byId.get(childId);
    if (!person || person.generation !== 2 || person.is_partner) continue;
    cousins.push(person);
    seen.add(person.person_id);
  }

  const extras = people
    .filter(
      (p) =>
        p.generation === 2 &&
        !p.is_partner &&
        p.branch_id === branchId &&
        !seen.has(p.person_id),
    )
    .sort(birthOrder);
  cousins.push(...extras);

  return cousins.map((cousin) => {
    const partner = people.find((p) => p.is_partner && p.partner_of === cousin.person_id);
    return {
      cousinId: cousin.person_id,
      partnerId: partner?.person_id,
      depth: partner ? COUSIN_SLOT_DEPTH + PAIR_SLOT_EXTRA : COUSIN_SLOT_DEPTH,
    };
  });
}

function shiftBranch(
  branchId: string,
  primaryId: string,
  deltaY: number,
  positions: Map<string, { x: number; y: number }>,
  people: Person[],
) {
  for (const person of people) {
    const inBranch =
      person.branch_id === branchId ||
      (Boolean(person.is_partner) && person.partner_of === primaryId);
    const pos = positions.get(person.person_id);
    if (!inBranch || !pos) continue;
    positions.set(person.person_id, { x: pos.x, y: pos.y + deltaY });
  }
}

function resolveLaneCollisions(
  positions: Map<string, { x: number; y: number }>,
  people: Person[],
  gen1: Person[],
) {
  const primaries = gen1.filter((p) => !p.is_partner);
  if (primaries.length <= 1) return;

  let lastBottom = -Infinity;
  const minGap = 10;

  const sorted = [...primaries].sort(
    (a, b) => (positions.get(a.person_id)?.y ?? 0) - (positions.get(b.person_id)?.y ?? 0),
  );

  for (const primary of sorted) {
    const point = positions.get(primary.person_id);
    if (!point) continue;
    const radius = footprintRadius(primary);
    const top = point.y - radius;
    const neededTop = lastBottom + minGap;
    const delta = Math.max(0, neededTop - top);
    if (delta > 0 && primary.branch_id) {
      shiftBranch(primary.branch_id, primary.person_id, delta, positions, people);
    }
    const newY = positions.get(primary.person_id)?.y ?? point.y;
    lastBottom = newY + radius;
  }
}

function buildEdges(
  people: Person[],
  byId: Map<string, Person>,
  positions: Map<string, { x: number; y: number }>,
  gen0: Person[],
  gen1: Person[],
): TreeEdge[] {
  const edges: TreeEdge[] = [];
  const seen = new Set<string>();
  const add = (from: string, to: string) => {
    const key = `${from}->${to}`;
    if (seen.has(key)) return;
    if (!positions.has(from) || !positions.has(to)) return;
    seen.add(key);
    edges.push({ from, to });
  };

  if (gen0.length === 2) {
    add(gen0[0]!.person_id, gen0[1]!.person_id);
  }
  for (const child of gen1) {
    if (gen0[0]) add(gen0[0].person_id, child.person_id);
  }

  for (const person of people) {
    if (!positions.has(person.person_id)) continue;
    if (person.is_partner && person.partner_of) {
      add(person.partner_of, person.person_id);
    }
    for (const parentId of person.parent_ids) {
      if (byId.get(parentId)?.generation === 0) continue;
      add(parentId, person.person_id);
    }
  }
  return edges;
}

export function computeTreeLayout(
  people: Person[],
  siblingOrder: string[],
): TreeLayoutResult {
  const byId = new Map(people.map((p) => [p.person_id, p]));
  const gen0 = people.filter((p) => p.generation === 0);
  const orderedGen1 = siblingOrder
    .map((id) => byId.get(id))
    .filter((p): p is Person => Boolean(p));
  // Fall back when meta.sibling_order is missing/stale so the tree isn't blank.
  const gen1 =
    orderedGen1.length > 0
      ? orderedGen1
      : people.filter((p) => p.generation === 1 && !p.is_partner).sort(birthOrder);

  const positions = new Map<string, { x: number; y: number }>();
  let cursorY = 80;
  const gen1X = GEN0_X + H_SPACING;
  const gen2BaseX = gen1X + H_SPACING;

  for (const gen1Person of gen1) {
    const spouse = gen1Person.spouse_ids
      .map((id) => byId.get(id))
      .find((p) => p?.is_partner);
    const slots = branchSlots(gen1Person, people, byId);
    const laneCenterY = cursorY + LANE_HEIGHT / 2;

    placePair(
      gen1Person.person_id,
      spouse?.person_id,
      gen1X,
      laneCenterY,
      positions,
      byId,
    );

    let slotX = gen2BaseX;
    for (const slot of slots) {
      const slotCenter = slotX + slot.depth / 2;
      placePair(slot.cousinId, slot.partnerId, slotCenter, laneCenterY, positions, byId);

      const childIds = gen3ChildIds(slot.cousinId, people);
      placeChildrenRow(childIds, slotX + slot.depth + 18, laneCenterY, positions);
      slotX += slot.depth + childRowWidth(childIds) + 20;
    }

    cursorY += LANE_HEIGHT + BRANCH_GAP;
  }

  resolveLaneCollisions(positions, people, gen1);

  const branchYs = gen1.map((p) => positions.get(p.person_id)?.y ?? 240);
  const treeMidY =
    branchYs.length > 0
      ? branchYs.reduce((a, b) => a + b, 0) / branchYs.length
      : 240;

  if (gen0.length === 2) {
    placePairVertical(
      gen0[0]!.person_id,
      gen0[1]!.person_id,
      GEN0_X,
      treeMidY,
      positions,
      byId,
    );
  } else if (gen0[0]) {
    positions.set(gen0[0].person_id, { x: GEN0_X, y: treeMidY });
  }

  const ys = [...positions.values()].map((p) => p.y);
  const paddedMinY = (ys.length ? Math.min(...ys) : 0) - 72;
  if (paddedMinY < 72) {
    const shift = 72 - paddedMinY;
    for (const [id, pos] of positions) {
      positions.set(id, { x: pos.x, y: pos.y + shift });
    }
  }

  const edges = buildEdges(people, byId, positions, gen0, gen1);

  const nodes: TreeNodeLayout[] = [];
  for (const [personId, pos] of positions) {
    const person = byId.get(personId);
    if (!person) continue;
    nodes.push({
      personId,
      x: pos.x,
      y: pos.y,
      label: treeLabel(person),
      diameter: nodeDiameter(person),
    });
  }

  const xs = nodes.map((n) => n.x);
  const nodeYs = nodes.map((n) => n.y);
  const maxX = (xs.length ? Math.max(...xs) : 400) + 160;
  const maxY = (nodeYs.length ? Math.max(...nodeYs) : 600) + 160;
  const width = Math.max(maxX + 80, 520);
  const height = Math.max(maxY + LABEL_BLOCK_HEIGHT + 60, 400);

  return { nodes, edges, width, height };
}
